// src/hooks/useMembers.js
import {useEffect, useState} from 'react';
import {get, onValue, ref, remove, set, update} from 'firebase/database';
import {database} from '../config/firebase';
import {useAuth} from './useAuth';
import {encodeEmail} from "../utils/utils";

export const useMembers = (churchId = null) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {currentUser, userRole} = useAuth();

    useEffect(() => {
        if (!currentUser) {
            setMembers([]);
            setLoading(false);
            return;
        }

        let membersRef;
        let unsubscribe;

        const fetchMembers = async () => {
            try {
                setLoading(true);

                // Déterminer quelle référence utiliser en fonction du rôle et de l'église
                if (userRole.role === 'admin') {
                    if (churchId) {
                        // Admin veut voir les membres d'une église spécifique
                        membersRef = ref(database, `churchMembers/${churchId}`);
                    } else {
                        // Si pas d'église spécifiée, commencer par obtenir la liste de tous les utilisateurs
                        // C'est une opération coûteuse, donc elle est réservée aux admins
                        const usersRef = ref(database, 'users');
                        const snapshot = await get(usersRef);

                        if (snapshot.exists()) {
                            const allUsers = snapshot.val();
                            const usersList = [];

                            for (const [uid, userData] of Object.entries(allUsers)) {
                                usersList.push({
                                    uid,
                                    ...userData.profile
                                });
                            }

                            setMembers(usersList);
                            setLoading(false);
                            return;
                        } else {
                            setMembers([]);
                            setLoading(false);
                            return;
                        }
                    }
                } else if (userRole.role === 'church_admin') {
                    // Admin d'église voit uniquement les membres de son église
                    const adminChurchId = userRole.churchId;
                    membersRef = ref(database, `churchMembers/${adminChurchId}`);
                } else {
                    // Utilisateur standard ne peut voir que son propre profil
                    setMembers([]);
                    setLoading(false);
                    return;
                }

                // Écouter les changements dans la liste des membres de l'église
                unsubscribe = onValue(membersRef, async (snapshot) => {
                    if (snapshot.exists()) {
                        const memberIds = Object.keys(snapshot.val());
                        const membersList = [];

                        // Récupérer les détails de chaque membre
                        for (const uid of memberIds) {
                            const userRef = ref(database, `users/${uid}/profile`);
                            const userSnapshot = await get(userRef);

                            if (userSnapshot.exists()) {
                                membersList.push({
                                    uid,
                                    ...userSnapshot.val()
                                });
                            }
                        }

                        setMembers(membersList);
                    } else {
                        setMembers([]);
                    }

                    setLoading(false);
                }, (error) => {
                    console.error("Erreur lors de la récupération des membres:", error);
                    setError(error.message);
                    setLoading(false);
                });
            } catch (error) {
                console.error("Erreur:", error);
                setError(error.message);
                setLoading(false);
            }
        };

        fetchMembers();

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [currentUser, userRole, churchId]);

    // Ajout/Mise à jour d'un membre (utilisé principalement par les admins)
    const updateMember = async (uid, userData) => {
        try {
            const userRef = ref(database, `users/${uid}/profile`);
            const oldDataSnapshot = await get(userRef);

            if (!oldDataSnapshot.exists()) {
                throw new Error("Membre non trouvé");
            }

            const oldData = oldDataSnapshot.val();

            // Mettre à jour le profil de l'utilisateur
            await update(userRef, {
                ...userData,
                updatedAt: Date.now()
            });

            // Si l'email ou le téléphone a changé, mettre à jour les tables de référence
            if (oldData.email !== userData.email) {
                if (oldData.email) {
                    // Supprimer l'ancienne référence (avec l'email encodé)
                    const oldEncodedEmail = encodeEmail(oldData.email);
                    await remove(ref(database, `userLookup/byEmail/${oldEncodedEmail}`));
                }

                if (userData.email) {
                    // Ajouter la nouvelle référence (avec l'email encodé)
                    const newEncodedEmail = encodeEmail(userData.email);
                    await set(ref(database, `userLookup/byEmail/${newEncodedEmail}`), uid);
                }
            }
            if (oldData.telephone !== userData.telephone) {
                await remove(ref(database, `userLookup/byPhone/${oldData.telephone}`));
                await set(ref(database, `userLookup/byPhone/${userData.telephone}`), uid);
            }

            // Si l'église a changé, mettre à jour les appartenances et les statistiques
            if (oldData.churchId !== userData.churchId) {
                // Retirer de l'ancienne église
                await remove(ref(database, `churchMembers/${oldData.churchId}/${uid}`));

                // Ajouter à la nouvelle église
                await set(ref(database, `churchMembers/${userData.churchId}/${uid}`), true);

                // Mettre à jour les statistiques des églises
                await updateChurchStats(oldData.churchId, -1, oldData.sexe, oldData.baptise);
                await updateChurchStats(userData.churchId, 1, userData.sexe, userData.baptise);
            } else if (oldData.sexe !== userData.sexe || oldData.baptise !== userData.baptise) {
                // Si le sexe ou le statut de baptême a changé, mettre à jour les statistiques
                await updateChurchStatsDifference(userData.churchId, oldData, userData);
            }

            // Si la profession a changé, mettre à jour les statistiques de profession
            if (oldData.profession !== userData.profession) {
                await updateProfessionStats(oldData.profession, -1);
                await updateProfessionStats(userData.profession, 1);
            }

            return {success: true};
        } catch (error) {
            console.error("Erreur lors de la mise à jour du membre:", error);
            return {success: false, error: error.message};
        }
    };

    // Supprimer un membre
    const deleteMember = async (uid) => {
        try {
            // Récupérer les données du membre avant suppression
            const userRef = ref(database, `users/${uid}/profile`);
            const userSnapshot = await get(userRef);

            if (!userSnapshot.exists()) {
                throw new Error("Membre non trouvé");
            }

            const userData = userSnapshot.val();

            // Supprimer le membre de l'église
            await remove(ref(database, `churchMembers/${userData.churchId}/${uid}`));

            // Supprimer les références de recherche
            if (userData.email) {
                const encodedEmail = encodeEmail(userData.email);
                await remove(ref(database, `userLookup/byEmail/${encodedEmail}`));
            }
            await remove(ref(database, `userLookup/byPhone/${userData.telephone}`));

            // Mettre à jour les statistiques
            await updateChurchStats(userData.churchId, -1, userData.sexe, userData.baptise);
            await updateGlobalStats(-1, userData.sexe, userData.baptise);
            await updateProfessionStats(userData.profession, -1);

            // Supprimer le profil utilisateur
            await remove(userRef);

            return {success: true};
        } catch (error) {
            console.error("Erreur lors de la suppression du membre:", error);
            return {success: false, error: error.message};
        }
    };

    // Fonction utilitaire pour mettre à jour les statistiques d'une église
    const updateChurchStats = async (churchId, delta, sexe, baptise) => {
        const statsRef = ref(database, `statistics/churches/${churchId}`);
        const statsSnapshot = await get(statsRef);

        if (statsSnapshot.exists()) {
            const stats = statsSnapshot.val();
            // Calculer les nouvelles valeurs en s'assurant qu'elles ne sont jamais négatives
            const newTotal = Math.max(0, (stats.totalMembres || 0) + delta);
            const newHommes = sexe === 'M' ? Math.max(0, (stats.hommes || 0) + delta) : (stats.hommes || 0);
            const newFemmes = sexe === 'F' ? Math.max(0, (stats.femmes || 0) + delta) : (stats.femmes || 0);
            const newBaptises = baptise ? Math.max(0, (stats.baptises || 0) + delta) : (stats.baptises || 0);

            await update(statsRef, {
                totalMembres: newTotal,
                hommes: newHommes,
                femmes: newFemmes,
                baptises: newBaptises
            });
        } else if (delta > 0) {
            // Si les stats n'existent pas et que c'est un ajout, créer l'entrée
            await set(statsRef, {
                totalMembres: delta,
                hommes: sexe === 'M' ? delta : 0,
                femmes: sexe === 'F' ? delta : 0,
                baptises: baptise ? delta : 0
            });
        }

        // Mettre à jour également les statistiques globales
        await updateGlobalStats(delta, sexe, baptise);
    };

    // Mettre à jour les statistiques globales
    const updateGlobalStats = async (delta, sexe, baptise) => {
        const statsRef = ref(database, `statistics/global`);
        const statsSnapshot = await get(statsRef);

        if (statsSnapshot.exists()) {
            const stats = statsSnapshot.val();
            // Calculer les nouvelles valeurs en s'assurant qu'elles ne sont jamais négatives
            const newTotal = Math.max(0, (stats.totalMembres || 0) + delta);
            const newHommes = sexe === 'M' ? Math.max(0, (stats.hommes || 0) + delta) : (stats.hommes || 0);
            const newFemmes = sexe === 'F' ? Math.max(0, (stats.femmes || 0) + delta) : (stats.femmes || 0);
            const newBaptises = baptise ? Math.max(0, (stats.baptises || 0) + delta) : (stats.baptises || 0);

            await update(statsRef, {
                totalMembres: newTotal,
                hommes: newHommes,
                femmes: newFemmes,
                baptises: newBaptises
            });
        } else if (delta > 0) {
            await set(statsRef, {
                totalMembres: delta,
                hommes: sexe === 'M' ? delta : 0,
                femmes: sexe === 'F' ? delta : 0,
                baptises: baptise ? delta : 0
            });
        }
    };

    // Mettre à jour les statistiques de profession
    const updateProfessionStats = async (profession, delta) => {
        if (!profession) return;

        const professionRef = ref(database, `statistics/professions/${profession}`);
        const snapshot = await get(professionRef);

        if (snapshot.exists()) {
            const count = snapshot.val();
            const newCount = count + delta;

            if (newCount <= 0) {
                // Supprimer l'entrée si le compte atteint zéro ou devient négatif
                await remove(professionRef);
            } else {
                await set(professionRef, newCount);
            }
        } else if (delta > 0) {
            await set(professionRef, delta);
        }
    };

    // Mettre à jour les statistiques quand le sexe ou le statut de baptême change
    const updateChurchStatsDifference = async (churchId, oldData, newData) => {
        const statsRef = ref(database, `statistics/churches/${churchId}`);
        const globalStatsRef = ref(database, `statistics/global`);

        // Mise à jour des statistiques de l'église
        const churchStatsSnapshot = await get(statsRef);
        if (churchStatsSnapshot.exists()) {
            const stats = churchStatsSnapshot.val();
            const updates = {};

            // Ajuster les statistiques de sexe si changé
            if (oldData.sexe !== newData.sexe) {
                if (oldData.sexe === 'M') updates.hommes = (stats.hommes || 0) - 1;
                if (oldData.sexe === 'F') updates.femmes = (stats.femmes || 0) - 1;
                if (newData.sexe === 'M') updates.hommes = ((stats.hommes || 0) + 1) - (oldData.sexe === 'M' ? 1 : 0);
                if (newData.sexe === 'F') updates.femmes = ((stats.femmes || 0) + 1) - (oldData.sexe === 'F' ? 1 : 0);
            }

            // Ajuster les statistiques de baptême si changé
            if (oldData.baptise !== newData.baptise) {
                if (oldData.baptise) updates.baptises = (stats.baptises || 0) - 1;
                if (newData.baptise) updates.baptises = (stats.baptises || 0) + 1;
            }

            await update(statsRef, updates);
        }

        // Mise à jour des statistiques globales
        const globalStatsSnapshot = await get(globalStatsRef);
        if (globalStatsSnapshot.exists()) {
            const stats = globalStatsSnapshot.val();
            const updates = {};

            // Ajuster les statistiques de sexe si changé
            if (oldData.sexe !== newData.sexe) {
                if (oldData.sexe === 'M') updates.hommes = (stats.hommes || 0) - 1;
                if (oldData.sexe === 'F') updates.femmes = (stats.femmes || 0) - 1;
                if (newData.sexe === 'M') updates.hommes = ((stats.hommes || 0) + 1) - (oldData.sexe === 'M' ? 1 : 0);
                if (newData.sexe === 'F') updates.femmes = ((stats.femmes || 0) + 1) - (oldData.sexe === 'F' ? 1 : 0);
            }

            // Ajuster les statistiques de baptême si changé
            if (oldData.baptise !== newData.baptise) {
                if (oldData.baptise) updates.baptises = (stats.baptises || 0) - 1;
                if (newData.baptise) updates.baptises = (stats.baptises || 0) + 1;
            }

            await update(globalStatsRef, updates);
        }
    };

    // Rechercher des membres
    const searchMembers = async (searchTerm, filters = {}) => {
        try {
            setLoading(true);

            // Si on est dans le contexte d'une église spécifique
            if (churchId || (userRole.role === 'church_admin' && userRole.churchId)) {
                const targetChurchId = churchId || userRole.churchId;
                const churchMembersRef = ref(database, `churchMembers/${targetChurchId}`);
                const churchMembersSnapshot = await get(churchMembersRef);

                if (churchMembersSnapshot.exists()) {
                    const memberPromises = Object.keys(churchMembersSnapshot.val()).map(async (uid) => {
                        const userRef = ref(database, `users/${uid}/profile`);
                        const userSnapshot = await get(userRef);

                        if (userSnapshot.exists()) {
                            return {
                                uid,
                                ...userSnapshot.val()
                            };
                        }
                        return null;
                    });

                    const allMembers = (await Promise.all(memberPromises)).filter(m => m !== null);

                    // Appliquer la recherche et les filtres
                    const filtered = allMembers.filter(member => {
                        // Recherche textuelle
                        const matchesSearch = !searchTerm ||
                            (member.nom && member.nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (member.prenom && member.prenom.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (member.telephone && member.telephone.includes(searchTerm)) ||
                            (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()));

                        // Filtres supplémentaires
                        const matchesGender = !filters.sexe || member.sexe === filters.sexe;
                        const matchesBaptized = filters.baptise === undefined || member.baptise === filters.baptise;
                        const matchesProfession = !filters.profession || member.profession === filters.profession;

                        return matchesSearch && matchesGender && matchesBaptized && matchesProfession;
                    });

                    setMembers(filtered);
                } else {
                    setMembers([]);
                }
            } else if (userRole.role === 'admin') {
                // Pour les admins sans filtre d'église, utiliser un index de recherche
                // Cette approche est moins efficace mais nécessaire pour les recherches globales
                // Dans un environnement de production, envisagez d'utiliser Algolia ou une solution similaire

                if (searchTerm && searchTerm.includes('@')) {
                    // Recherche par email
                    const encodedEmail = encodeEmail(searchTerm);
                    const emailRef = ref(database, `userLookup/byEmail/${encodedEmail}`);
                    const emailSnapshot = await get(emailRef);

                    if (emailSnapshot.exists()) {
                        const uid = emailSnapshot.val();
                        const userRef = ref(database, `users/${uid}/profile`);
                        const userSnapshot = await get(userRef);

                        if (userSnapshot.exists()) {
                            setMembers([{uid, ...userSnapshot.val()}]);
                        } else {
                            setMembers([]);
                        }
                    } else {
                        // Recherche partielle d'email - plus coûteuse
                        const usersRef = ref(database, 'users');
                        const usersSnapshot = await get(usersRef);

                        if (usersSnapshot.exists()) {
                            const filtered = [];

                            usersSnapshot.forEach(childSnapshot => {
                                const uid = childSnapshot.key;
                                const profile = childSnapshot.val().profile;

                                if (profile && profile.email && profile.email.toLowerCase().includes(searchTerm.toLowerCase())) {
                                    filtered.push({uid, ...profile});
                                }
                            });

                            setMembers(filtered);
                        } else {
                            setMembers([]);
                        }
                    }
                } else if (searchTerm && searchTerm.match(/^\+?[0-9]{8,}$/)) {
                    // Recherche par téléphone
                    const phoneRef = ref(database, `userLookup/byPhone/${searchTerm}`);
                    const phoneSnapshot = await get(phoneRef);

                    if (phoneSnapshot.exists()) {
                        const uid = phoneSnapshot.val();
                        const userRef = ref(database, `users/${uid}/profile`);
                        const userSnapshot = await get(userRef);

                        if (userSnapshot.exists()) {
                            setMembers([{uid, ...userSnapshot.val()}]);
                        } else {
                            setMembers([]);
                        }
                    } else {
                        // Recherche partielle de téléphone - plus coûteuse
                        const usersRef = ref(database, 'users');
                        const usersSnapshot = await get(usersRef);

                        if (usersSnapshot.exists()) {
                            const filtered = [];

                            usersSnapshot.forEach(childSnapshot => {
                                const uid = childSnapshot.key;
                                const profile = childSnapshot.val().profile;

                                if (profile && profile.telephone && profile.telephone.includes(searchTerm)) {
                                    filtered.push({uid, ...profile});
                                }
                            });

                            setMembers(filtered);
                        } else {
                            setMembers([]);
                        }
                    }
                } else {
                    // Recherche par nom ou autre critère
                    const usersRef = ref(database, 'users');
                    const usersSnapshot = await get(usersRef);

                    if (usersSnapshot.exists()) {
                        const filtered = [];

                        usersSnapshot.forEach(childSnapshot => {
                            const uid = childSnapshot.key;
                            const profile = childSnapshot.val().profile;

                            if (profile) {
                                const matchesSearch = !searchTerm ||
                                    (profile.nom && profile.nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                    (profile.prenom && profile.prenom.toLowerCase().includes(searchTerm.toLowerCase()));

                                const matchesGender = !filters.sexe || profile.sexe === filters.sexe;
                                const matchesBaptized = filters.baptise === undefined || profile.baptise === filters.baptise;
                                const matchesProfession = !filters.profession || profile.profession === filters.profession;
                                const matchesChurch = !filters.churchId || profile.churchId === filters.churchId;

                                if (matchesSearch && matchesGender && matchesBaptized && matchesProfession && matchesChurch) {
                                    filtered.push({uid, ...profile});
                                }
                            }
                        });

                        setMembers(filtered);
                    } else {
                        setMembers([]);
                    }
                }
            } else {
                // Pour les utilisateurs standards, ne renvoyer aucun résultat de recherche
                setMembers([]);
            }

            setLoading(false);
        } catch (error) {
            console.error("Erreur lors de la recherche:", error);
            setError(error.message);
            setLoading(false);
        }
    };

    return {
        members,
        loading,
        error,
        updateMember,
        deleteMember,
        searchMembers
    };
};
